import { useEffect, useState } from 'react';
import { trpc } from '@org/clients/school';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TextArea,
} from '@org/ui';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useTeacherId } from './teacher-picker';
import { FLAG_STYLES, type TeacherFlagLevel } from './student-flag-badges';

const LEVELS: TeacherFlagLevel[] = ['red', 'yellow', 'green'];
const MAX_COMMENT = 1000;

/** The teacher's reason categories, in the order she listed them. */
export const FLAG_REASONS = [
  'class_engagement',
  'work',
  'motivation',
  'learning_difficulty',
  'communication',
  'personal_life',
  'emotional_health',
  'family_logistics',
  'schedule_conflict',
  'other',
] as const;
export type FlagReason = (typeof FLAG_REASONS)[number];

const isFlagReason = (value: string): value is FlagReason => (FLAG_REASONS as readonly string[]).includes(value);

type StudentFlagDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  studentId: string;
  studentName: string;
  currentFlag?: TeacherFlagLevel | null;
};

/**
 * Lets the teacher give a student a red / yellow / green flag with a reason
 * category and a short explanation. Every save appends a new entry; the
 * profile shows the history.
 */
export function StudentFlagDialog({
  open,
  onOpenChange,
  studentId,
  studentName,
  currentFlag,
}: StudentFlagDialogProps) {
  const { t } = useTranslation('admin');
  const teacherId = useTeacherId();
  const utils = trpc.useUtils();

  const [level, setLevel] = useState<TeacherFlagLevel>(currentFlag ?? 'yellow');
  const [reason, setReason] = useState<FlagReason | null>(null);
  const [comment, setComment] = useState('');

  // Start fresh each time the dialog opens.
  useEffect(() => {
    if (open) {
      setLevel(currentFlag ?? 'yellow');
      setReason(null);
      setComment('');
    }
  }, [open, currentFlag]);

  const setFlag = trpc.teacher.students.setFlag.useMutation({
    onSuccess: () => {
      utils.teacher.students.list.invalidate();
      utils.teacher.students.profile.invalidate();
      utils.teacher.students.groupOverview.invalidate();
      toast.success(t('teacher.flags.dialog.saved'));
      onOpenChange(false);
    },
    onError: () => {
      toast.error(t('teacher.flags.dialog.error'));
    },
  });

  const trimmed = comment.trim();
  const canSave = reason !== null && trimmed.length > 0 && trimmed.length <= MAX_COMMENT && !setFlag.isPending;

  const handleSave = () => {
    if (!canSave || reason === null) return;
    setFlag.mutate({ teacherId, studentId, flag: level, reason, comment: trimmed });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('teacher.flags.dialog.title', { name: studentName })}</DialogTitle>
          <DialogDescription>{t('teacher.flags.dialog.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-[0.2em] text-gray-500">{t('teacher.flags.dialog.level')}</p>
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t('teacher.flags.dialog.level')}>
              {LEVELS.map((option) => {
                const style = FLAG_STYLES[option];
                const selected = option === level;
                return (
                  <button
                    key={option}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => setLevel(option)}
                    className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition ${style.pill} ${
                      selected ? `ring-2 ${style.ring}` : 'border-transparent opacity-70 hover:opacity-100'
                    }`}
                  >
                    <span className={`h-2.5 w-2.5 rounded-full ${style.dot}`} />
                    {t(`teacher.flags.levels.${option}`)}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="student-flag-reason" className="text-xs uppercase tracking-[0.2em] text-gray-500">
              {t('teacher.flags.dialog.reason')}
            </label>
            <Select
              value={reason ?? ''}
              onValueChange={(value) => setReason(isFlagReason(value) ? value : null)}
            >
              <SelectTrigger
                id="student-flag-reason"
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-[#cf2e2e] focus:outline-none"
              >
                <SelectValue placeholder={t('teacher.flags.dialog.reasonPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {FLAG_REASONS.map((option) => (
                  <SelectItem key={option} value={option}>
                    {t(`teacher.flags.reasonCategories.${option}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {reason && <p className="text-xs text-gray-500">{t(`teacher.flags.reasonHints.${reason}`)}</p>}
          </div>

          <div className="space-y-2">
            <label htmlFor="student-flag-comment" className="text-xs uppercase tracking-[0.2em] text-gray-500">
              {t('teacher.flags.dialog.comment')}
            </label>
            <TextArea
              id="student-flag-comment"
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              placeholder={t('teacher.flags.dialog.commentPlaceholder')}
              maxLength={MAX_COMMENT}
              rows={4}
              required
              className="border-gray-200 focus-visible:ring-[#cf2e2e]"
            />
          </div>
        </div>

        <DialogFooter className="mt-4 flex gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={setFlag.isPending}>
            {t('teacher.flags.dialog.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!canSave}
            isLoading={setFlag.isPending}
            loadingText={t('common.status.saving')}
          >
            {t('teacher.flags.dialog.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default StudentFlagDialog;
